import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";

import type { MyPupProjectConfigurationDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { integrationMutations } from "@/queries/integrations/mutations";
import { useIntegrationQueries } from "@/queries/integrations/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export type MyPupFormValues = MyPupProjectConfigurationDto;

export function MyPupPage(): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();
  const hasPermission = usePermission();
  const integrationQueries = useIntegrationQueries();

  const { data, isFetching, error } = useQuery(integrationQueries.myPupConfiguration());
  const mutation = integrationMutations.useEditMyPupConfiguration();

  if (!hasPermission((x) => x.integrations.canManageMyPup)) {
    return <ErrorPage status={404} />;
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  if (isFetching) {
    return <FullSizeLoader withPadding />;
  }

  const name = t("page.integration.mypup.title");

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={name}
      breadcrumbs={
        <Breadcrumbs
          pages={[{ name: t("page.integration.title"), to: routes.projects.integrations({ slug }) }, { name }]}
        />
      }
    >
      <MyPupForm
        defaultValues={{ enabled: data?.enabled || false }}
        isSubmitting={mutation.isPending}
        onSubmit={mutation.mutate}
      />
    </DocumentPage>
  );
}

function MyPupForm({
  defaultValues,
  isSubmitting,
  onSubmit,
}: {
  defaultValues: Partial<MyPupFormValues>;
  isSubmitting: boolean;
  onSubmit: (data: MyPupFormValues) => void;
}): React.ReactNode {
  const { t } = useTranslation();
  const formMethods = useForm<MyPupFormValues>({ defaultValues });

  return (
    <Form.Root formMethods={formMethods} onSubmit={onSubmit}>
      <Form.Container title={t("page.integration.mypup.title")}>
        <Form.Body>
          <FormCheckbox<MyPupFormValues, "enabled"> id="enabled" name="enabled" label={t("common.action.enable")} />
        </Form.Body>
        <Form.Footer
          renderPrimaryAction={() => (
            <Button type="submit" isLoading={isSubmitting}>
              {t("common.action.save")}
            </Button>
          )}
        />
      </Form.Container>
    </Form.Root>
  );
}
